import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Modal,
  MenuItem,
  Divider,
  Typography,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { updateLocalTheme } from "../redux/slices/themeSlice";
import {
  SettingsModalInterface,
} from "../assets/interfaces/interfaces";
import { AppDispatch } from "../redux/store";
import { fetchDeleteUser } from "../redux/slices/deleteUserSlice";
import { useDeleteAllChatsMutation, usePaginatedChats } from "./Sidebar/hooks";
import { CloseRounded, EditNotificationsRounded, SettingsRounded } from "@mui/icons-material";
import ToggleButtonsGroup from "./Shared/ToggleButtonsGroup";
import DeleteModal from "./Shared/DeleteModal";
import classNames from "classnames";
import { updateNotifPreferences } from "../redux/slices/userSlice";
import { showNotification } from "../redux/slices/notificationSlice";
import { theme } from "../theme";

const settingsMenuItems = [
  {
    id: 1,
    label: "General",
    icon: <SettingsRounded className="!h-5 !w-5" />,
  },
  {
    id: 2,
    label: "Notifications",
    icon: <EditNotificationsRounded className="!h-5 !w-5" />,
  },
];

const SettingsModal: React.FC<SettingsModalInterface> = ({
  open,
  setOpen,
  handleProfileMenuClose,
  clearAllChatData,
  handleSignOut,
  initialActiveMenu,
}) => {
  const handleClose = () => {
    setOpen(false);
    handleProfileMenuClose();
  };
  const accountDeletedStatus = useSelector(
    (state: { deleteUser }) => state?.deleteUser?.status,
  );
  const { localTheme } = useSelector((state: { theme }) => state.theme);
  const dispatch: AppDispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeMenuItem, setActiveMenuItem] = useState(isMobile ? null : 1);

  const user = useSelector((state: { auth }) => state.auth.currentUser);
  const { notifications, status } = useSelector((state: { user }) => state.user);

  useEffect(() => {
    if (initialActiveMenu) {
      setActiveMenuItem(initialActiveMenu);
    }
  }, [initialActiveMenu]);

  // assume both true if notifications is undefined
  const normalizedNotifs
  = status === "succeeded"
    ? notifications?.email ?? { marketing: true, account: true }
    : { marketing: false, account: false };
  const userId = user?.userId || "";

  const [userNotifs, setUserNotifs] = useState(normalizedNotifs);

  useEffect(() => {
    setUserNotifs(normalizedNotifs);
  }, [notifications]);

  const handleUpdateEmailPrefs = async (field: "account" | "marketing") => {
    const updated = { ...userNotifs, [field]: !userNotifs[field] };
    setUserNotifs(updated);
    try {
      await dispatch(updateNotifPreferences(updated));
      const isSubscribed = updated[field];
      dispatch(showNotification ({ message: `You’ve successfully ${isSubscribed ? "subscribed" : "unsubscribed"} to ${field} emails.`, severity: "success", horizontal: "right" }));
    }
    catch (err) {
      console.error(err);
      dispatch(showNotification ({ message: "Changes not saved, please try again.", severity: "error", horizontal: "right" }));
    }
  };

  const handleDeleteUser = () => {
    dispatch(fetchDeleteUser());
    setConfirmOpen(false);
    setDeleteUser(false);
  };

  useEffect(() => {
    if (accountDeletedStatus === "succeeded") {
      handleSignOut();
    }
  }, [accountDeletedStatus]);

  const deleteAllChats = useDeleteAllChatsMutation();

  const handleDeleteAllChats = async () => {
    try {
      deleteAllChats.mutate({ userId });
      clearAllChatData();
    }
    catch (error) {
      console.error("Failed to delete all chats:", error);
    }
  };

  const { data: chatHistory } = usePaginatedChats({ userId: user?.userId });
  return (
    <>
      {!confirmOpen && (
        <Modal open={open} onClose={handleClose}>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="shadow w-modal-mobile sm:w-modal-md lg:w-modal-lg sm:!h-[510px] text-mui-black-87 dark:text-white">
              <div className="w-full sm:flex justify-between h-full">

                {(!isMobile || !activeMenuItem) && (
                  <section className="w-full rounded-lg flex flex-col py-6 sm:w-[186px] bg-white dark:bg-background/paper-elevation-24 sm:rounded-s-lg sm:rounded-e-none">
                    <Typography variant="h6/medium-500" className="px-4 pb-2 sm:pb-5 items-center">Settings</Typography>
                    <div className="flex flex-col gap-y-1 pt-2">
                      {settingsMenuItems.map(setting => (
                        <MenuItem
                          key={setting.id}
                          onClick={() => setActiveMenuItem(setting.id)}
                          selected={setting.id === activeMenuItem}
                          className={classNames({
                            "!bg-tgpt-secondary-states-hover !rounded-[4px]": setting.id === activeMenuItem,
                          })}
                        >
                          <div className="py-1 flex items-center gap-x-[12px]">
                            <div className="!text-action-active ">{setting.icon}</div>
                            <div className="dark:text-white">{setting.label}</div>
                          </div>
                        </MenuItem>
                      ))}
                    </div>
                  </section>
                )}

                {activeMenuItem && (
                  <div className="rounded-lg dark:bg-background/paper-elevation-16 bg-white py-6 px-4 flex flex-col sm:rounded-e-lg sm:rounded-s-none flex-1">
                    <div className="relative sm:pb-6">
                      <IconButton
                        size="small"
                        onClick={handleClose}
                        className="cross-icon"
                        sx={{ position: "absolute", top: 0, right: 8 }}
                      >
                        <CloseRounded className="!fill-action-active !w-5 !h-5" />
                      </IconButton>
                    </div>

                    {activeMenuItem === 1 && (
                      <section className="flex flex-col gap-y-6 sm:pt-4">
                        <Typography variant="h6/medium-500" className="sm:hidden">General</Typography>
                        <div className="flex flex-col gap-y-2 items-start">
                          <Typography variant="body2/regular-400">Theme</Typography>
                          <ToggleButtonsGroup
                            stateVar={localTheme}
                            setter={(newValue: "light" | "dark" | "system") =>
                              dispatch(updateLocalTheme(newValue))}
                            options={["system", "dark", "light"]}
                          />
                        </div>
                        <Divider />
                        <div className="flex justify-between items-center">
                          <Typography variant="body2/regular-400">Delete all chats</Typography>
                          <Button
                            disabled={!chatHistory?.pages?.length}
                            onClick={() => {
                              setOpen(false); // Close the first modal
                              setConfirmOpen(true); // Open the second modal after a short delay
                            }}
                            variant="contained"
                            className="!bg-error-main !normal-case"
                            size="large"
                          >
                            Delete
                          </Button>
                        </div>
                      </section>
                    )}

                    {activeMenuItem === 2 && (
                      <section className="flex flex-col gap-y-4">
                        <Typography variant="body1/regular-400" className="w-[247px] sm:w-full">Select the type of emails you wish to receive</Typography>
                        <div className="flex items-center gap-x-2">
                          <Checkbox
                            checked={userNotifs.account}
                            className={
                              classNames("!p-0 text-mui-black-60 dark:text-mui-white-70",
                                {
                                  "!text-tgpt-secondary-main": userNotifs.account,
                                },
                              )
                            }
                            onChange={() => handleUpdateEmailPrefs("account")}
                          />
                          <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">Product and Feature Announcements</Typography>
                        </div>
                        <div className="flex items-center gap-x-2">
                          <Checkbox
                            checked={userNotifs.marketing}
                            className={
                              classNames("!p-0 text-mui-black-60 dark:text-mui-white-70",
                                {
                                  "!text-tgpt-secondary-main": userNotifs.marketing,
                                },
                              )
                            }
                            onChange={() => handleUpdateEmailPrefs("marketing")}
                          />
                          <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">Offers and Promotions</Typography>
                        </div>
                        <Divider />
                        <Typography variant="body2/regular-400" className="dark:text-mui-white-70 pt-[9px]">Feature-specific notifications can be managed within each feature.</Typography>
                      </section>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
      <DeleteModal
        open={confirmOpen}
        handleDelete={deleteUser ? handleDeleteUser : handleDeleteAllChats}
        handleCloseWarning={() => {
          setDeleteUser(false);
          setConfirmOpen(false);
        }}
        mainText="Clear Conversation History"
        subText="Are you sure you would like to delete all your chats? The deletion is irreversible."
      />
    </>
  );
};
export default SettingsModal;
